<script setup lang="ts">
const props = defineProps<{
  recipe: {
    id: number
    title?: string
    external?: boolean
    url?: string
    thumbnailUrl?: string
    slug?: string
    score?: number
    color?: string
    website_name?: string
    flyers_ingredients?: any[]
    flyers_ingredients_new?: any[]
    matching_ingredients?: any[]
    match_count?: number
    ingredients_match?: string
    tags_match?: string
  }
}>()

const modalStore = useModalStore()
const userStore = useUserStore()
const toast = useToast()
const config = useRuntimeConfig()
const localePath = useLocalePath()
const overlay = useOverlayWithAd()

const LazyNoteModal = defineAsyncComponent(() => import('~/components/modals/NoteModal.vue'))
const LazyRecipeBoxModal = defineAsyncComponent(() => import('~/components/modals/RecipeBoxModal.vue'))

// Computed properties pour vérifier l'état des notes et classifications
const hasNote = computed(() => {
  if (!userStore.user || !userStore.user.notes) {
    return false
  }

  if (props.recipe.external) {
    return userStore.user.notes.includes(`e${props.recipe.id}`)
  }
  else {
    return userStore.user.notes.includes(props.recipe.id)
  }
})

const savedCount = computed(() => {
  if (!userStore.user || !userStore.user.saved_recipes)
    return 0

  const recipeKey = props.recipe.external ? `e${props.recipe.id}` : props.recipe.id.toString()
  const savedLists = userStore.user.saved_recipes[recipeKey]

  return savedLists ? savedLists.length : 0
})

function openPlannerModal() {
  // Vérifier si l'utilisateur est connecté et a un abonnement Extra
  if (!userStore.user || !userStore.user.extra) {
    toast.add({
      title: 'Fonctionnalité Extra',
      description: 'Le planificateur de menus est disponible uniquement avec un abonnement Extra.',
      color: 'warning',
      icon: 'i-mdi-star',
      actions: [
        {
          label: 'Découvrir l\'Extra',
          color: 'primary',
          variant: 'solid',

          to: localePath({ name: 'ExtraPage' }),

        },
      ],
    })
    return
  }

  // Si l'utilisateur est connecté et a un abonnement Extra, ouvrir la modal
  modalStore.openPlannerCalendarModal(props.recipe)
}

function openNoteModal() {
  // Vérifier si l'utilisateur est connecté et a un abonnement Extra
  if (!userStore.user || !userStore.user.extra) {
    toast.add({
      title: 'Fonctionnalité Extra',
      description: 'L\'annotation de recettes est disponible uniquement avec un abonnement Extra.',
      color: 'warning',
      icon: 'i-mdi-star',
      actions: [
        {
          label: 'Découvrir l\'Extra',
          color: 'primary',
          variant: 'solid',
          to: localePath({ name: 'ExtraPage' }),
        },
      ],
    })
    return
  }

  // Si l'utilisateur est connecté et a un abonnement Extra, ouvrir la modal
  const modal = overlay.create(LazyNoteModal, {
    props: {
      recipeId: props.recipe.id,
      recipeType: props.recipe.external ? 'external' : 'recipe',
    },
  })
  modal.open()
}

function openRecipeBoxModal() {
  // Vérifier si l'utilisateur est connecté
  if (!userStore.user) {
    toast.add({
      title: 'Connexion requise',
      color: 'error',
      description: 'Vous devez être connecté pour classer une recette.',
      actions: [{
        label: 'Se connecter',
        color: 'secondary',
        variant: 'outline',
        onClick: () => {
          modalStore.openLoginModal()
        },
      }, {
        label: 'Créer un compte',
        color: 'secondary',
        variant: 'solid',
        to: localePath({ name: 'RegisterPage' }),
      }],
      icon: 'i-lucide-alert-circle',
    })
    return
  }

  // Ouvrir la modal de boîte de recettes
  const modal = overlay.create(LazyRecipeBoxModal, {
    props: {
      recipeId: props.recipe.id,
      recipeType: props.recipe.external ? 'external' : 'recipe',
    },
  })
  modal.open()
}
</script>

<template>
  <div class="flex flex-col text-center bg-white rounded-lg shadow hover:shadow-md h-full">
    <div class="relative aspect-w-36 aspect-h-25">
      <a v-if="recipe.external" :href="recipe.url" target="_blank+"><img class="flex-shrink-0 w-full mx-auto bg-black rounded-t-lg" :src="`${config.public.backendCdnUrl}${recipe.thumbnailUrl}`" alt="" loading="lazy"></a>
      <ULink v-else as="a" :to="$localePath({ name: 'RecipePage', params: { id: recipe.id, slug: recipe.slug } })">
        <img class="flex-shrink-0 w-full mx-auto bg-black rounded-t-lg" :src="`${config.public.backendCdnUrl}${recipe.thumbnailUrl}`" alt="">
        <span v-if="recipe.score" class="absolute p-2 font-bold bottom-2 right-2 rounded-xl opacity-90 text-white" :style="`background-color:${recipe.color}`">{{ recipe.score }}%</span>
      </ULink>
    </div>
    <div :class="[recipe.flyers_ingredients ? '' : 'flex-1']">
      <div class="m-3 text-left">
        <div class="text-xs text-poivre-400 flex justify-between">
          <span>{{ recipe.website_name }}</span> <span v-if="recipe.external" class="inline-flex items-center px-2.5 py-0 rounded-full text-xs font-medium bg-sorbet text-sauge">Importée</span>
        </div>
        <a v-if="recipe.external" :href="recipe.url" class="text-base/5 font-semibold text-left text-poivre block" target="_blank">{{ recipe.title }}</a>
        <ULink v-else as="a" :to="$localePath({ name: 'RecipePage', params: { id: recipe.id, slug: recipe.slug } })" class="text-base/5 font-semibold text-left text-poivre block line-clamp-2">
          {{ recipe.title }}
        </ULink>
      </div>
    </div>
    <div v-if="recipe.matching_ingredients && recipe.matching_ingredients.length > 0" class="p-3 text-left border-t border-gray-200">
      <span class="text-sm font-bold text-green-600">Ingrédients du vide-frigo:</span>
      <div class="flex flex-wrap gap-1 mt-1">
        <UBadge v-for="ingredient in recipe.matching_ingredients" :key="ingredient.id" size="md" color="tertiary" variant="outline">
          {{ ingredient.name }}
        </UBadge>
      </div>
    </div>
    <div v-if="recipe.ingredients_match || recipe.tags_match" class="p-3 text-left border-t ">
      <div v-if="recipe.ingredients_match" class="text-sm font-bold text-sauge flex items-center">
        <UIcon name="i-mdi-check-bold" class="mr-2" /> <span v-html="recipe.ingredients_match" />
      </div>
      <div v-if="recipe.tags_match" class="text-sm font-bold text-sauge flex items-center">
        <UIcon name="i-mdi-tag" class="mr-2" /> <span v-html="recipe.tags_match" />
      </div>
    </div>
    <div v-if="recipe.flyers_ingredients" class="h-full p-3 text-left border-t border-gray-200">
      <div v-for="store in recipe.flyers_ingredients" :key="store" class="mb-2">
        <div class="text-sm font-semibold" :style="`color:${store[0].text_color}`">
          Rabais chez {{ store[0].store_name }}
        </div>
        <div class="flex flex-wrap gap-1 mt-1">
          <UBadge v-for="ingredient in store" :key="ingredient.id" size="md" color="tertiary" variant="outline" class="cursor-pointe" @click="modalStore.openItemModal(ingredient.flyer_item_id)">
            {{ ingredient.name }}
          </UBadge>
        </div>
      </div>
    </div>
    <div v-if="recipe.flyers_ingredients_new" class="h-full p-3 text-left border-t border-gray-200">
      <div v-for="store in recipe.flyers_ingredients_new" :key="store" class="mb-2">
        <div class="flex items-center justify-between gap-2">
          <span class="text-sm font-semibold" :style="`color:${store.text_color}`">Rabais chez {{ store.store_name }}</span>
        </div>
        <div class="flex flex-wrap gap-1 mt-1">
          <UBadge
            v-for="ingredient in store.matches"
            :key="ingredient.id"
            size="md"
            color="tertiary"
            variant="outline"
            class="cursor-pointer"
            :class="ingredient.highlight === 1 ? 'bg-sorbet-100' : undefined"
            @click="modalStore.openItemModal(ingredient.flyer_item_id)"
          >
            {{ ingredient.name }}
          </UBadge>
        </div>
      </div>
    </div>
    <div class="flex border-t border-poivre-200 divide-x divide-poivre-200">
      <div class="flex flex-1 w-0">
        <button class="relative flex flex-col items-center justify-center flex-1 w-full py-2 text-poivre-500 hover:text-poivre-700 *inner-poivre-focus" @click="openPlannerModal">
          <div class="flex">
            <UIcon name="i-mdi-calendar-plus" class="w-5 h-5" aria-hidden="true" />
          </div>
          <div class="text-xs mt-0.5">
            Planifier
          </div>
        </button>
      </div>
      <div class="flex flex-1 w-0">
        <button class="relative flex flex-col items-center justify-center flex-1 w-full py-2 text-poivre-500 hover:text-poivre-700 *inner-poivre-focus" @click="openNoteModal">
          <div class="flex">
            <UIcon name="i-mdi-note-plus-outline" class="w-5 h-5" aria-hidden="true" />
            <UChip v-if="hasNote" size="lg" color="success" variant="solid" />
          </div>
          <div class="text-xs mt-0.5">
            Annoter
          </div>
        </button>
      </div>
      <div class="flex flex-1 w-0">
        <button class="relative flex flex-col items-center justify-center flex-1 w-full py-2 text-poivre-500 hover:text-poivre-700 *inner-poivre-focus" @click="openRecipeBoxModal">
          <div class="flex">
            <UIcon name="i-mdi-archive-star-outline" class="w-5 h-5" aria-hidden="true" />
            <UChip v-if="savedCount > 0" size="lg" color="info" variant="solid" />
          </div>
          <div class="text-xs mt-0.5">
            Classer
          </div>
        </button>
      </div>
    </div>
  </div>
</template>
